import {
    Component,
    OnInit,
    Input,
    Output,
    EventEmitter,
    OnChanges,
    SimpleChanges,
    effect,
    ViewChild,
    ElementRef,
    OnDestroy,
    DestroyRef,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { FormsModule } from '@angular/forms';
import { MonFilterService } from '../../services/filter.service';
import { FilterObject, FilterResponse, FilterOption } from '../../types/filter.type';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { FloatLabelModule } from 'primeng/floatlabel';
import { MultiSelectModule, MultiSelectLazyLoadEvent, MultiSelectFilterEvent } from 'primeng/multiselect';
import { CheckboxModule } from 'primeng/checkbox';
import { PopoverModule, Popover } from 'primeng/popover';
import { FocusTrapModule } from 'primeng/focustrap';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

const FILTER_SEARCH_DEBOUNCE_TIME = 300;
const FILTER_LOAD_DEBOUNCE_TIME = 300;
const OPEN_DROPDOWN_SELECTORS = '.p-select-open, .p-multiselect-open';
const DROPDOWN_PANEL_SELECTORS = '.p-multiselect, .p-select';

@Component({
    selector: 'mon-filter',
    standalone: true,
    templateUrl: './filter.component.html',
    styleUrl: './filter.component.scss',
    imports: [
        CommonModule,
        TranslocoModule,
        FormsModule,
        ButtonModule,
        SelectModule,
        FloatLabelModule,
        MultiSelectModule,
        CheckboxModule,
        PopoverModule,
        FocusTrapModule,
    ],
})
export class FilterComponent implements OnInit, OnChanges, OnDestroy {
    @Input() filterObject: FilterObject = {};
    @Input() filterOrder: string[] = [];
    @Input() pageKey: string = '';
    @Input() clearFiltersTrigger: boolean = false;
    @Output() filtersApplied = new EventEmitter<FilterResponse>();
    @Output() loadMorefilterOptions = new EventEmitter<{ filterKey: string, page: number }>();

    @ViewChild('filterPopover') filterPopover!: Popover;
    @ViewChild('filterContent') filterContent!: ElementRef;
    @ViewChild('filterToggleButton') filterToggleButton!: ElementRef;

    filterResponse: FilterResponse = {};
    saveFilters: boolean = false;
    totalfilters: number = 0;
    batchSize: number = 20;
    filterCollection: { key: string, value: FilterObject[string] } [] = [];
    private readonly searchSubject$ = new Subject<{ filterKey: string, search: string }>();
    private readonly lazyLoadSubject$ = new Subject<{ filterKey: string, page: number }>();
    private loadedPages: Record<string, Set<number>> = {};
    private initialLoadDone = false;
    private focusOutListener?: ((event: FocusEvent) => void) | undefined;
    private escKeyListener?: ((event: KeyboardEvent) => void) | undefined;
    private readonly destroyRef = inject(DestroyRef);


    constructor (
        public readonly filterService: MonFilterService,
    ) {
        effect(() => {
            const filters = this.filterService.filterResponse();
            this.filterResponse = filters;
            if (this.initialLoadDone) {
                this.getTotalFiltersApplied();
                this.filtersApplied.emit(filters);
            }
        });
    }

    ngOnInit (): void {
        if (!this.pageKey) {
            throw new Error('Input "pageKey" is required for FilterComponent');
        }

        this.searchSubject$.pipe(
            debounceTime(FILTER_SEARCH_DEBOUNCE_TIME),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(async ({ filterKey, search }) => {
            const filterDef = this.filterObject[filterKey];
            if (filterDef?.searchFn) {
                const results = await filterDef.searchFn(search);

                let selected: FilterOption[] = [];
                const selectedValue = this.filterResponse[filterKey];
                if (Array.isArray(selectedValue)) {
                    selected = selectedValue;
                } else if (selectedValue) {
                    selected = [selectedValue];
                }

                const merged = [...selected.filter(sel => !results.some(opt => opt.value === sel.value)), ...results];
                filterDef.options = merged;
            }
        });

        this.lazyLoadSubject$.pipe(
            debounceTime(FILTER_LOAD_DEBOUNCE_TIME),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(({ filterKey, page }) => {
            if (!this.loadedPages[filterKey]) {
                this.loadedPages[filterKey] = new Set();
            }
            if (!this.loadedPages[filterKey].has(page)) {
                this.loadedPages[filterKey].add(page);
                this.loadMorefilterOptions.emit({ filterKey, page });
            }
        });
    }

    ngOnDestroy (): void {
        this.removeFocusOutListener();
        this.removeEscKeyListener();
        this.searchSubject$.complete();
        this.lazyLoadSubject$.complete();
    }

    ngOnChanges (changes: SimpleChanges): void {
        if (
            changes['filterObject'] ||
            changes['filterOrder']
        ) {
            this.filterCollection = this.getFilterCollection();
        }

        if (changes['filterObject']) {
            this.loadedPages = {};
            if (Object.keys(this.filterObject).length > 0) {
                this.filterResponse = {};
                for (const [filterKey, filterValue] of Object.entries(this.filterObject)) {
                    this.filterResponse[filterKey] = filterValue.type === 'select' ? null : [];
                }

                const tempFilterResponse = JSON.parse(this.filterService.getFilters(this.pageKey) || '{}');
                this.validateFilters(tempFilterResponse).then(validatedFilters => {
                    this.filterService.filterResponse.set(validatedFilters);
                    this.initialLoadDone = true;
                });
            }
        }

        if (changes['clearFiltersTrigger']?.currentValue === true) {
            this.clearFilter();
        }
    }

    toggleFilters (event: PointerEvent | MouseEvent): void {
        this.filterPopover.toggle(event);
    }

    onPopoverShow (): void {
        this.filterResponse = structuredClone(this.filterService.filterResponse());
        this.setupFocusOutListener();
        this.setupEscKeyListener();

        setTimeout(() => {
            this.focusFirstElement();
        }, 100);
    }

    onPopoverHide (): void {
        this.removeFocusOutListener();
        this.removeEscKeyListener();

        setTimeout(() => {
            this.filterToggleButton?.nativeElement?.focus();
        }, 0);
    }

    private setupFocusOutListener (): void {
        this.removeFocusOutListener();

        this.focusOutListener = (event: FocusEvent): void => {
            const popoverContainer = this.filterPopover.container;
            const target = event.target as Element;

            if (popoverContainer && target) {
                const isInsidePopover = popoverContainer.contains(target);
                const isMultiSelectDropdown = this.isInsideDropdownPanel(target);

                if (!isInsidePopover && !isMultiSelectDropdown) {
                    this.closeFilters();
                }
            }
        };

        document.addEventListener('focusin', this.focusOutListener);
    }

    private removeFocusOutListener (): void {
        if (this.focusOutListener) {
            document.removeEventListener('focusin', this.focusOutListener);
            this.focusOutListener = undefined;
        }
    }

    private setupEscKeyListener (): void {
        this.removeEscKeyListener();

        this.escKeyListener = (event: KeyboardEvent): void => {
            if (event.key !== 'Escape') {
                return;
            }

            const openPanels = document.querySelectorAll(OPEN_DROPDOWN_SELECTORS);
            const hasOpenPanels = Array.from(openPanels).some((panel) => (panel as HTMLElement).offsetParent !== null);

            if (!hasOpenPanels) {
                event.preventDefault();
                event.stopPropagation();
                this.closeFilters();
            }
        };

        document.addEventListener('keydown', this.escKeyListener, { capture: true });
    }

    private removeEscKeyListener (): void {
        if (this.escKeyListener) {
            document.removeEventListener('keydown', this.escKeyListener, { capture: true });
            this.escKeyListener = undefined;
        }
    }

    private isInsideDropdownPanel (target: Element): boolean {
        return target.closest(DROPDOWN_PANEL_SELECTORS) !== null;
    }

    private focusFirstElement (): void {
        if (this.filterContent?.nativeElement) {
            const focusableElements = this.filterContent.nativeElement.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
            );

            if (focusableElements.length > 0) {
                (focusableElements[0] as HTMLElement).focus();
            } else {
                this.filterContent.nativeElement.focus();
            }
        }
    }

    closeFilters (): void {
        this.filterPopover.hide();
    }

    clearFilter (): void {
        this.filterResponse = {};
        this.filterService.clearFilters(this.pageKey);
        this.getTotalFiltersApplied();
        this.filtersApplied.emit(this.filterResponse);
    }

    applyFilters (): void {
        if (this.saveFilters) {
            this.filterService.saveFilters(this.pageKey, JSON.stringify(this.filterResponse));
            this.saveFilters = false;
        }
        this.getTotalFiltersApplied();
        const appliedFilters = structuredClone(this.filterResponse) as FilterResponse;
        this.filterService.filterResponse.set(appliedFilters);
        this.filtersApplied.emit(appliedFilters);
        this.closeFilters();
    }

    getTotalFiltersApplied (): number {
        this.totalfilters = Object.values(this.filterResponse).reduce((sum, val) => {
            if (Array.isArray(val)) {
                return sum + val.length;
            } else if (val !== null && val !== undefined) {
                return sum + 1;
            }
            return sum;
        }, 0);
        return this.totalfilters;
    }

    async validateFilters (filterResponse: FilterResponse) {
        const verifiedFilters: FilterResponse = {};
        for (const key of Object.keys(filterResponse)) {
            const filterValue = filterResponse[key];
            const filterDef = this.filterObject[key];
            if (!filterDef) {
                continue;
            }
            if (
                filterValue &&
                filterValue !== null &&
                filterDef.validateFn &&
                !(Array.isArray(filterValue) && filterValue.length === 0)
            ) {
                Object.assign(verifiedFilters, await filterDef.validateFn({ [key]: filterValue }));
            } else {
                Object.assign(verifiedFilters, { [key]: filterValue });
            }

            const fetchedOptions = Array.isArray(filterDef.options) ? filterDef.options : [];
            const mergedOptions = [...fetchedOptions];
            if (Array.isArray(verifiedFilters[key])) {
                for (const val of verifiedFilters[key]) {
                    const exists = mergedOptions.some(opt => opt === val || (opt?.name !== undefined && opt.name === val?.name));
                    if (!exists && val) {
                        mergedOptions.unshift(val);
                    }
                }
            } else if (!Array.isArray(verifiedFilters[key]) && verifiedFilters[key] !== null && verifiedFilters[key] !== undefined) {
                const valid = mergedOptions.some(
                    opt =>
                        opt === verifiedFilters[key] ||
                        (
                            opt?.name !== undefined &&
                            opt.name === (verifiedFilters[key] as { name?: string }).name
                        ),
                );
                if (!valid && verifiedFilters[key]) {
                    mergedOptions.unshift(verifiedFilters[key]);
                }
            }
            this.filterObject[key].options = mergedOptions;
        }

        this.filterService.saveFilters(this.pageKey, JSON.stringify(verifiedFilters));
        return verifiedFilters;
    }

    loadMoreOptions (event: MultiSelectLazyLoadEvent, filterKey: string) {
        const nextPage = Math.floor(event.last / this.batchSize) + 1;
        // Avoid duplicate page 1 fetch
        if (nextPage <= 1) {
            return;
        }
        this.lazyLoadSubject$.next({ filterKey, page: nextPage });
    }

    async searchFilter (event: MultiSelectFilterEvent , filterKey: string) {
        const searchText = (event.filter || '').trim();
        // Reset loaded pages for new search so lazy loading works with fresh dataset
        this.loadedPages[filterKey] = new Set();
        this.searchSubject$.next({ filterKey, search: searchText });
    }

    trackByFilter (index: number, item: { key: string; value: any }): string {
        return item.key;
    }

    private getFilterCollection () {
        if (!this.filterObject || Object.keys(this.filterObject).length === 0) {
            return [];
        }

        if (this.filterOrder.length > 0) {
            return this.filterOrder.map(key => {
                return {
                    key,
                    value: this.filterObject[key],
                };
            });
        }

        return Object.entries(this.filterObject).map(([key, value]) => {
            return {
                key,
                value,
            };
        });
    }

}
